import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react';
import Editor from '@monaco-editor/react';
import React, { useEffect, useState } from 'react';

import {
  date as commonDate,
  error as commonError,
  finleyAssert,
  finleyAssertUnreachable,
  getErrorMessage,
  typeguards as commonTypeguards,
} from '@finley-technologies/common';
import {
  CreditAgreement,
  Reporting,
  typeguards,
} from '@finley-technologies/finley-types';

import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import {
  formatSelectOptions,
  Select,
} from '~/packages/common-frontend/components/Select';
import { errorToastConfig } from '~/packages/common-frontend/constants';

/**
 * Since triggering from Report Config rather than external client,
 * defaulting to "QA" mode for now as it better aligns with pure user action.
 * Can consider in the future if we want to expose "Live" mode as well here
 */
const DEFAULT_WORKLEASE_MODE: Reporting.WorkleaseMode = 'QA';

const REPORT_TYPES_REQUIRING_REPORTING_DATE: CreditAgreement.DomainEntity.ReportConfigType[] = [
  'monthlyServicerReport',
];

const getDefaultWorkleaseConfig = ({
  reportConfigType,
  userId,
}: {
  reportConfigType: CreditAgreement.DomainEntity.ReportConfigType;
  userId: string;
}): Reporting.ReportingGenerateQaWorkleaseConfig => {
  switch (reportConfigType) {
    case 'assetVerification':
    case 'monthlyServicerReport':
      return null;
    case 'borrowingBaseServicerReport':
      return { borrowingBaseReportId: null };
    case 'borrowingBaseCertificate':
    case 'fundingRequestServicerReport':
    case 'noticeOfBorrowing':
    case 'noticeOfPrepayment':
    case 'releaseNotice':
    case 'fundingNotice':
      return { fundingRequestId: null, userId };
    case 'receivableResponseTapePreviewServicerReport':
      return { receivableResponseTapePreviewId: null, userId };
    case 'receivablesSaleAgreement':
      return { assignedLoanTapeId: null };
    case 'invoice':
      return {
        invoiceId: null,
        previewInvoice: null,
        previewInvoiceComponentsWithInvoiceItems: null,
        userId,
      };
    case 'disbursementNotice':
      return {
        invoiceId: 'placeholder',
        capitalProviderOrganizationId: 'placeholder',
        userId,
      };
    case 'paymentInKindNotice':
      return {
        invoicePaymentInKindItemId: 'placeholder',
        userId,
      };
    default:
      return finleyAssertUnreachable(reportConfigType);
  }
};

const REPORT_CONFIG_TYPE_OPTIONS = formatSelectOptions(
  CreditAgreement.DomainEntity.ReportConfigTypeIterable,
);

type ReportingGenerateWorkleaseConfigInputProps = {
  config: Reporting.ReportingGenerateQaWorkleaseConfig;
  setConfig: React.Dispatch<Reporting.ReportingGenerateQaWorkleaseConfig>;
  reportConfigType: CreditAgreement.DomainEntity.ReportConfigType;
  userId: string;
};
const ReportingGenerateWorkleaseConfigInput = ({
  config,
  reportConfigType,
  setConfig,
  userId,
}: ReportingGenerateWorkleaseConfigInputProps) => {
  switch (reportConfigType) {
    case 'borrowingBaseServicerReport':
      finleyAssert(
        typeguards.reporting.isBorrowingBaseServicerReportingGenerateQaWorkleaseConfig(
          config,
        ),
        new commonError.TypeError({
          resourceType:
            'BorrowingBaseServicerReportingGenerateQaWorkleaseConfig',
          typeguardName:
            'isBorrowingBaseServicerReportingGenerateQaWorkleaseConfig',
        }),
      );
      return (
        <Flex alignItems="center" mb="6">
          <FormLabel w="48" m="0">
            Optional Entity ID:
          </FormLabel>
          <Input
            width="56"
            type="text"
            value={config.borrowingBaseReportId ?? ''}
            onChange={(event) => {
              // borrowingBaseReportId is nullable only for QA mode
              return setConfig({
                borrowingBaseReportId: event.target.value || null,
              });
            }}
          />
        </Flex>
      );
    case 'borrowingBaseCertificate':
    case 'fundingRequestServicerReport':
    case 'noticeOfBorrowing':
    case 'noticeOfPrepayment':
    case 'releaseNotice':
    case 'fundingNotice':
      finleyAssert(
        typeguards.reporting.isFundingRequestReportingGenerateQaWorkleaseConfig(
          config,
        ),
        new commonError.TypeError({
          resourceType: 'FundingRequestReportingGenerateQaWorkleaseConfig',
          typeguardName: 'isFundingRequestReportingGenerateQaWorkleaseConfig',
        }),
      );
      return (
        <Flex alignItems="center" mb="6">
          <FormLabel w="48" m="0">
            Optional Entity ID:
          </FormLabel>
          <Input
            width="56"
            type="text"
            value={config.fundingRequestId ?? ''}
            onChange={(event) => {
              // fundingRequestId is nullable only for QA mode
              return setConfig({
                fundingRequestId: event.target.value || null,
                userId,
              });
            }}
          />
        </Flex>
      );
    case 'assetVerification':
    case 'monthlyServicerReport':
      return null;
    case 'invoice':
      finleyAssert(
        typeguards.reporting.isInvoiceReportingGenerateQaWorkleaseConfig(
          config,
        ),
        new commonError.TypeError({
          resourceType: 'InvoiceReportingGenerateQaWorkleaseConfig',
          typeguardName: 'isInvoiceReportingGenerateQaWorkleaseConfig',
        }),
      );
      return (
        <Flex alignItems="center" mb="6">
          <FormLabel w="48" m="0">
            Optional Entity ID:
          </FormLabel>
          <Input
            width="56"
            type="text"
            value={config.invoiceId ?? ''}
            onChange={(event) => {
              return setConfig({
                invoiceId: event.target.value || null,
                previewInvoice: null,
                previewInvoiceComponentsWithInvoiceItems: null,
                userId,
              });
            }}
          />
        </Flex>
      );
    case 'disbursementNotice':
    case 'paymentInKindNotice':
      return null; // @dani todo: add disbursement notice & PIK notice QA mode if needed
    case 'receivableResponseTapePreviewServicerReport':
      finleyAssert(
        typeguards.reporting.isReceivableResponseTapePreviewReportingGenerateQaWorkleaseConfig(
          config,
        ),
        new commonError.TypeError({
          resourceType:
            'ReceivableResponseTapePreviewReportingGenerateQaWorkleaseConfig',
          typeguardName:
            'isReceivableResponseTapePreviewReportingGenerateQaWorkleaseConfig',
        }),
      );
      return (
        <Flex alignItems="center" mb="6">
          <FormLabel w="48" m="0">
            Optional Entity ID:
          </FormLabel>
          <Input
            width="56"
            type="text"
            value={config.receivableResponseTapePreviewId ?? ''}
            onChange={(event) => {
              return setConfig({
                receivableResponseTapePreviewId: event.target.value || null,
                userId,
              });
            }}
          />
        </Flex>
      );
    case 'receivablesSaleAgreement':
      finleyAssert(
        typeguards.reporting.isReceivablesSaleAgreementReportingGenerateQaWorkleaseConfig(
          config,
        ),
        new commonError.TypeError({
          resourceType:
            'ReceivablesSaleAgreementReportingGenerateQaWorkleaseConfig',
          typeguardName:
            'isReceivablesSaleAgreementReportingGenerateQaWorkleaseConfig',
        }),
      );
      return (
        <Flex alignItems="center" mb="6">
          <FormLabel w="48" m="0">
            Optional Entity ID:
          </FormLabel>
          <Input
            width="56"
            type="text"
            value={config.assignedLoanTapeId ?? ''}
            onChange={(event) => {
              // assignedLoanTapeId is nullable only for QA mode
              return setConfig({
                assignedLoanTapeId: event.target.value || null,
              });
            }}
          />
        </Flex>
      );
    default:
      return finleyAssertUnreachable(reportConfigType);
  }
};

type ReportingGenerateWorkleaseModalProps = {
  title: string;
  isOpen: boolean;
  onCancel: () => void;
  onExecute: (params: {
    worklease: Reporting.ReportingGenerateWorklease;
    parameters: Reporting.ReportingGenerateWorkleaseStatusParameterMetadata | null;
  }) => void;
  reportConfigTypes: CreditAgreement.DomainEntity.ReportConfigType[];
  reportName: string;
  creditFacilityId: string | null;
  creditAgreementId: string | null;
  reportTemplateFileType: CreditAgreement.Config.Reporting.ReportTemplateFileType;
  userId: string;
  initiatingOrganizationId: string;
  showParameterEditor: boolean;
};
export const ReportingGenerateWorkleaseModal = ({
  title,
  isOpen,
  onCancel,
  onExecute,
  reportConfigTypes,
  reportName,
  creditFacilityId,
  creditAgreementId,
  reportTemplateFileType,
  userId,
  initiatingOrganizationId,
  showParameterEditor,
}: ReportingGenerateWorkleaseModalProps) => {
  const toast = useToast();
  const [
    reportConfigType,
    setReportConfigType,
  ] = useState<CreditAgreement.DomainEntity.ReportConfigType>(
    reportConfigTypes[0],
  );

  const [
    config,
    setConfig,
  ] = useState<Reporting.ReportingGenerateQaWorkleaseConfig>(
    getDefaultWorkleaseConfig({
      reportConfigType,
      userId,
    }),
  );
  const [reportingDate, setReportingDate] = useState<string>(
    commonDate.currentDateStr(),
  );
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isEditorOpen, setIsEditorOpen] = useState<boolean>(false);

  const [parameterString, setParameterString] = useState<string>('');

  const onFullExecute = () => {
    if (!creditFacilityId) {
      setErrorMessage('Missing credit facility ID');
      return;
    }
    if (!creditAgreementId) {
      setErrorMessage('Missing credit agreement ID');
      return;
    }
    if (!commonTypeguards.isFullDateStr(reportingDate)) {
      setErrorMessage(
        `"${reportingDate}" is not valid reporting date structure`,
      );
      return;
    }

    const worklease = {
      creditAgreementId,
      creditFacilityId,
      initiatingOrganizationId,
      // Note: for reportTypes that rely on entityId, reportingDate in
      // QA worklease is ignored, and will be replaced with whatever reportingDate is associated
      // with the entity specified, once known on the BE
      reportingDate,
      reportName,
      mode: DEFAULT_WORKLEASE_MODE,
      reportTemplateFileType,
      reportConfigType,
      config,
    };

    finleyAssert(
      typeguards.reporting.isReportingGenerateWorklease(worklease),
      new commonError.TypeError({
        resourceType: 'ReportingGenerateWorklease',
        typeguardName: 'isReportingGenerateWorklease',
      }),
    );

    let parameters: Reporting.ReportingGenerateWorkleaseStatusParameterMetadata | null = null;
    if (parameterString.length > 0) {
      try {
        parameters = JSON.parse(parameterString);

        const errors: string[] = [];

        finleyAssert(
          typeguards.reporting.isReportingGenerateWorkleaseStatusParameterMetadata(
            parameters,
            (schemaErrors) => {
              schemaErrors.forEach((e) => errors.push(e.toString()));
            },
          ),
          new commonError.FinleyError(errors.join(', ')),
        );
      } catch (err) {
        const message = getErrorMessage(err);
        errorToastConfig.title = `Parameter Error: ${message}`;
        toast(errorToastConfig);
        return;
      }
    }

    onExecute({ parameters, worklease });
    onCancel();
  };

  useEffect(() => {
    setErrorMessage('');
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onCancel();
      }}
      size="lg"
    >
      <ModalOverlay />
      <ModalContent p="5">
        <ModalHeader>{reportName}</ModalHeader>
        <ModalCloseButton />
        <ModalBody width="100%">
          <Box>
            <Text mb="6">{title}</Text>

            <Flex alignItems="center" mb="6">
              <FormLabel w="48" m="0">
                Execute Mode:
              </FormLabel>
              <Text px="4">{DEFAULT_WORKLEASE_MODE}</Text>
            </Flex>

            <Flex alignItems="center" mb="6">
              <FormLabel w="48" m="0">
                Report Config Type:
              </FormLabel>
              <Box w="56">
                <Select<CreditAgreement.DomainEntity.ReportConfigType>
                  value={reportConfigType}
                  options={REPORT_CONFIG_TYPE_OPTIONS}
                  onChange={(value) => {
                    if (value && value !== reportConfigType) {
                      setReportConfigType(value);
                      setConfig(
                        getDefaultWorkleaseConfig({
                          reportConfigType: value,
                          userId,
                        }),
                      );
                    }
                  }}
                />
              </Box>
            </Flex>

            {REPORT_TYPES_REQUIRING_REPORTING_DATE.includes(
              reportConfigType,
            ) && (
              <Flex alignItems="center" mb="6">
                <FormLabel w="48" m="0">
                  Reporting Date:
                </FormLabel>
                <Input
                  w="56"
                  type="text"
                  value={reportingDate}
                  onChange={(evt) => {
                    setReportingDate(evt.target.value);
                  }}
                />
              </Flex>
            )}

            <ReportingGenerateWorkleaseConfigInput
              config={config}
              setConfig={setConfig}
              reportConfigType={reportConfigType}
              userId={userId}
            />

            {!REPORT_TYPES_REQUIRING_REPORTING_DATE.includes(
              reportConfigType,
            ) && (
              <Text fontStyle="italic" fontSize="sm" mb="4">
                If optional entity is omitted, latest existing entity (and
                associated reportingDate) will be used.
              </Text>
            )}

            {showParameterEditor && (
              <Box mb="2">
                <HStack justifyContent="space-between">
                  <FormLabel w="48" mb="2">
                    Optional Parameters:
                  </FormLabel>
                  {isEditorOpen ? (
                    <IconButton
                      aria-label="close editor"
                      icon={<FiChevronUp />}
                      onClick={() => setIsEditorOpen(false)}
                      variant="ghost"
                    />
                  ) : (
                    <IconButton
                      aria-label="open editor"
                      icon={<FiChevronDown />}
                      onClick={() => setIsEditorOpen(true)}
                      variant="ghost"
                    />
                  )}
                </HStack>
                {isEditorOpen && (
                  <Box
                    resize="vertical"
                    minH="200px"
                    h="250px"
                    maxH="450px"
                    overflowY="auto"
                    pr="1"
                    pb="1"
                  >
                    <Editor
                      language="json"
                      value={parameterString}
                      onChange={(val) => setParameterString(val ?? '')}
                      theme="vs-dark"
                      options={{ minimap: { enabled: false } }}
                    />
                  </Box>
                )}
              </Box>
            )}

            {errorMessage && (
              <Alert status="error" mb="6" rounded="md">
                <AlertIcon />
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            )}

            <Flex justifyContent="center">
              <Button
                type="button"
                mr="5"
                isDisabled={!reportingDate}
                onClick={() => {
                  onFullExecute();
                }}
              >
                Execute
              </Button>
              <Button onClick={() => onCancel()}>Close</Button>
            </Flex>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
